import { isSameDay } from 'date-fns';
import { TFunction } from 'i18next';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskCardTask } from 'shared/types/task-card-task';
import { Timestamp } from 'shared/types/timestamp';
import { initialWeekStartsOn, WeekDays } from 'shared/types/week-days';

import { getActiveSchedule } from '../get-active-schedule';
import { getScheduleLabel } from '../get-schedule-label';
import { getTaskCheckedState } from '../get-task-checked-state';
import { getTaskCompletedState } from '../get-task-completed-state';
import { getDueLabel, getTaskDueDate } from '../get-task-due';
import { getTaskOverdue } from '../get-task-overdue';
import { parseDateTime } from '../parse-date-time';

type Options = {
  goals: Goal[];
  today: Timestamp;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  showRepeatingAsTodo?: boolean;
  t: TFunction;
  customId?: ID;
  showDue?: boolean | ((task: Task) => boolean);
  hideFutureDue?: boolean;
  hideTodayDue?: boolean;
  hidePastDue?: boolean;
  hideGoal?: boolean;
};

export const mapTaskToTaskCard = (
  task: Task,
  {
    goals,
    weekStartsOn,
    timeFormat,
    today,
    showRepeatingAsTodo,
    t,
    customId,
    showDue: showDueFn,
    hideFutureDue,
    hideTodayDue,
    hidePastDue,
    hideGoal,
  }: Options,
): TaskCardTask => {
  weekStartsOn = weekStartsOn ?? initialWeekStartsOn;

  const activeSchedule = getActiveSchedule(task.schedules, today);
  const scheduleString = activeSchedule
    ? getScheduleLabel(activeSchedule, t)
    : undefined;

  const showDue =
    typeof showDueFn === 'function' ? showDueFn(task) : !!showDueFn;
  const dueDate = showDue
    ? getTaskDueDate(task, {
        today,
        weekStartsOn,
      })
    : undefined;
  const due = getDueLabel(dueDate, {
    t,
    today,
    hideFutureDue,
    hideTodayDue,
    hidePastDue,
  });
  const overdue = showDue
    ? getTaskOverdue(task, { today, weekStartsOn })
    : undefined;
  const dueToday = !!(dueDate && isSameDay(dueDate, today));

  return {
    id: customId ?? task.id,
    checked: getTaskCheckedState(task, {
      today,
      showRepeatingAsTodo,
      weekStartsOn,
    }),
    strikeThrough: getTaskCompletedState(task, today, showRepeatingAsTodo),
    meta: {
      goal: !hideGoal
        ? goals.find(({ id }) => id === task.goalId)?.name
        : undefined,
      reminderTime: task.reminderTime
        ? parseDateTime(task.reminderTime, timeFormat)
        : undefined,
      schedule: scheduleString,
      due,
      overdue,
      dueToday,
    },
    raw: {
      dueDate,
      task,
    },
    fields: {
      name: task.name,
      type: task.type,
      schedules: task.schedules ?? undefined,
      reminderTime: task.reminderTime ?? undefined,
      goalId: task.goalId ?? undefined,
      priority: task.priority ?? undefined,
      endStrategy: {
        deadline: task.endStrategy?.deadline ?? undefined,
        completionCount: task.endStrategy?.completionCount ?? undefined,
      },
    },
    isFrozen: !!task.frozenAt,
  };
};
